/**
 * bpost Plug-in v3 API React Query hooks.
 * Auth: Public Key + Private Key + Shop URL (callback).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
};

export function useBpostConfig() {
    return useQuery({
        queryKey: ['bpost-config'],
        queryFn: async () => {
            const { data } = await axios.get(`${API}/bpost/config`, { headers: authHeaders() });
            return data;
        },
        staleTime: 60_000,
    });
}

export function useSaveBpostConfig() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ publicKey, privateKey, shopUrl }) => {
            const { data } = await axios.post(
                `${API}/bpost/config`,
                { public_key: publicKey, private_key: privateKey, shop_url: shopUrl },
                { headers: authHeaders() },
            );
            return data;  // {ok, public_key, token_expires, carrier_count, carriers}
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['bpost-config'] }),
    });
}

export function useDeleteBpostConfig() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data } = await axios.delete(`${API}/bpost/config`, { headers: authHeaders() });
            return data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['bpost-config'] }),
    });
}

export function useSaveBpostSender() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (sender) => {
            const { data } = await axios.post(
                `${API}/bpost/sender`,
                sender,
                { headers: authHeaders() },
            );
            return data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['bpost-config'] }),
    });
}

export function useBpostCarriers({ enabled = true } = {}) {
    return useQuery({
        queryKey: ['bpost-carriers'],
        queryFn: async () => {
            const { data } = await axios.get(`${API}/bpost/carriers`, { headers: authHeaders() });
            return data;
        },
        enabled,
        staleTime: 5 * 60_000,
        retry: false,
    });
}

export function useBpostTestShipment() {
    return useMutation({
        mutationFn: async () => {
            const { data } = await axios.post(
                `${API}/bpost/test-shipment`,
                {},
                { headers: authHeaders() },
            );
            return data;
        },
    });
}

export function useCreateBpostLabel() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ orderId, force = false }) => {
            const r = await axios.post(
                `${API}/bol/labels/bpost-create`,
                { order_id: orderId, force },
                {
                    headers: authHeaders(),
                    responseType: 'blob',
                    timeout: 90000,
                    validateStatus: () => true,  // 200+JSON ve 200+PDF ikisini birden ele al
                },
            );
            return r;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['bol-orders'] });
        },
    });
}

export function useDeleteBpostLabel() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ orderId }) => {
            const { data } = await axios.delete(
                `${API}/bol/labels/bpost/${orderId}`,
                { headers: authHeaders() },
            );
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['bol-orders'] });
        },
    });
}
